import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Share2, Eye, MapPin, Calendar, Clock, DollarSign, Star, Users } from 'lucide-react';
import Button from './ui/Button';
import Card from './ui/Card';

interface EventCardProps {
  event: {
    id: string;
    title: string;
    image: string;
    price: number;
    date: string;
    time: string;
    location: string;
    category: string;
    rating?: number;
    attendees?: number;
    tags?: string[];
  };
  onFavorite?: (eventId: string) => void;
  isFavorited?: boolean;
  onShare?: (eventId: string, eventTitle: string) => void;
  viewMode?: 'grid' | 'list';
}

const EventCard: React.FC<EventCardProps> = ({ 
  event, 
  onFavorite, 
  isFavorited = false,
  onShare,
  viewMode = 'grid'
}) => {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on action buttons
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('[role="button"]')) {
      return;
    }
    navigate(`/event/${event.id}`);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFavorite?.(event.id);
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare?.(event.id, event.title);
  };

  // Grid view (ticket style for larger screens, card style for mobile)
  if (viewMode === 'grid') {
    return (
      <Card 
        variant="glass" 
        hover 
        className="relative overflow-hidden group cursor-pointer"
        onClick={handleCardClick}
      >
        {/* Image Section */}
        <div className="relative w-full h-48 sm:h-56 md:h-64 overflow-hidden">
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-blue-500/20 animate-pulse" />
          )}
          <img
            src={imageError ? '/assets/placeholder-event.jpg' : event.image}
            alt={event.title}
            className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              setImageError(true);
              setImageLoaded(true);
            }}
            loading="lazy"
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          
          {/* Action Buttons */}
          <div className="absolute top-3 right-3 flex gap-2 z-10">
            <button
              onClick={handleFavoriteClick}
              className={`w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl backdrop-blur-md transition-all duration-300 hover:scale-110 active:scale-95 ${
                isFavorited
                  ? 'bg-red-500/90 text-white shadow-lg'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Heart size={20} className={isFavorited ? 'fill-current' : ''} />
            </button>
            {onShare && (
              <button
                onClick={handleShareClick}
                className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl backdrop-blur-md bg-white/20 text-white hover:bg-white/30 transition-all duration-300 hover:scale-110 active:scale-95"
                aria-label="Share event"
              >
                <Share2 size={20} />
              </button>
            )}
          </div>

          {/* Price Badge */}
          <div className="absolute bottom-3 left-3 glass-card-strong px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl backdrop-blur-md">
            <div className="flex items-center gap-1.5">
              <DollarSign size={16} className="text-yellow-400" />
              <span className="text-white font-bold text-sm sm:text-base">
                {event.price.toFixed(2)} JOD
              </span>
            </div>
          </div>

          {/* Category Badge */}
          <div className="absolute top-3 left-3 glass-card-strong px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-lg backdrop-blur-md">
            <span className="text-white text-xs sm:text-sm font-medium capitalize">
              {event.category}
            </span>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-4 sm:p-5 md:p-6">
          {/* Title */}
          <h3 
            className="font-bold text-lg sm:text-xl md:text-2xl mb-2 sm:mb-3 line-clamp-2 leading-tight"
            style={{color: 'var(--liquid-text-primary)'}}
          >
            {event.title}
          </h3>

          {/* Event Details */}
          <div className="space-y-2 sm:space-y-2.5 mb-4">
            <div className="flex items-center gap-2 text-white/80 text-sm sm:text-base">
              <Calendar size={16} className="text-yellow-400 flex-shrink-0" />
              <span>{event.date}</span>
            </div>
            <div className="flex items-center gap-2 text-white/80 text-sm sm:text-base">
              <Clock size={16} className="text-yellow-400 flex-shrink-0" />
              <span>{event.time}</span>
            </div>
            <div className="flex items-center gap-2 text-white/80 text-sm sm:text-base">
              <MapPin size={16} className="text-yellow-400 flex-shrink-0" />
              <span className="line-clamp-1">{event.location}</span>
            </div>
          </div>

          {/* Rating and Attendees */}
          {(event.rating || event.attendees) && (
            <div className="flex items-center gap-4 mb-4 text-sm sm:text-base">
              {event.rating && (
                <div className="flex items-center gap-1.5">
                  <Star size={16} className="text-yellow-400 fill-current" />
                  <span className="text-white/90 font-medium">{event.rating.toFixed(1)}</span>
                </div>
              )}
              {event.attendees && (
                <div className="flex items-center gap-1.5">
                  <Users size={16} className="text-blue-400" />
                  <span className="text-white/90">{event.attendees.toLocaleString()}</span>
                </div>
              )}
            </div>
          )}

          {/* View Details Button */}
          <Button
            variant="glass"
            size="lg"
            onClick={handleCardClick}
            className="w-full min-h-[44px] sm:min-h-[48px]"
            icon={<Eye size={18} />}
          >
            View Details
          </Button>
        </div>
      </Card>
    );
  }

  // List view (horizontal layout)
  return (
    <Card 
      variant="glass" 
      hover 
      className="relative overflow-hidden group cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
        {/* Image Section */}
        <div className="relative w-full sm:w-64 md:w-80 h-48 sm:h-auto sm:flex-shrink-0 overflow-hidden rounded-xl">
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-blue-500/20 animate-pulse" />
          )}
          <img
            src={imageError ? '/assets/placeholder-event.jpg' : event.image}
            alt={event.title}
            className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              setImageError(true);
              setImageLoaded(true);
            }}
            loading="lazy"
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/20 to-transparent sm:hidden" />
          
          {/* Action Buttons */}
          <div className="absolute top-3 right-3 flex gap-2 z-10">
            <button
              onClick={handleFavoriteClick}
              className={`w-10 h-10 flex items-center justify-center rounded-xl backdrop-blur-md transition-all duration-300 hover:scale-110 active:scale-95 ${
                isFavorited
                  ? 'bg-red-500/90 text-white shadow-lg'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Heart size={20} className={isFavorited ? 'fill-current' : ''} />
            </button>
            {onShare && (
              <button
                onClick={handleShareClick}
                className="w-10 h-10 flex items-center justify-center rounded-xl backdrop-blur-md bg-white/20 text-white hover:bg-white/30 transition-all duration-300 hover:scale-110 active:scale-95"
                aria-label="Share event"
              >
                <Share2 size={20} />
              </button>
            )}
          </div>

          {/* Price Badge */}
          <div className="absolute bottom-3 left-3 glass-card-strong px-3 py-1.5 rounded-xl backdrop-blur-md">
            <div className="flex items-center gap-1.5">
              <DollarSign size={16} className="text-yellow-400" />
              <span className="text-white font-bold text-sm">
                {event.price.toFixed(2)} JOD
              </span>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 p-4 sm:p-6 flex flex-col justify-between">
          <div>
            {/* Title and Category */}
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2.5 py-1 glass-card-strong rounded-lg text-xs font-medium text-white capitalize">
                    {event.category}
                  </span>
                </div>
                <h3 
                  className="font-bold text-xl sm:text-2xl md:text-3xl mb-3 line-clamp-2 leading-tight"
                  style={{color: 'var(--liquid-text-primary)'}}
                >
                  {event.title}
                </h3>
              </div>
            </div>

            {/* Event Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
              <div className="flex items-center gap-2 text-white/80 text-sm sm:text-base">
                <Calendar size={18} className="text-yellow-400 flex-shrink-0" />
                <span>{event.date}</span>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-sm sm:text-base">
                <Clock size={18} className="text-yellow-400 flex-shrink-0" />
                <span>{event.time}</span>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-sm sm:text-base sm:col-span-2">
                <MapPin size={18} className="text-yellow-400 flex-shrink-0" />
                <span className="line-clamp-1">{event.location}</span>
              </div>
            </div>

            {/* Rating and Attendees */}
            {(event.rating || event.attendees) && (
              <div className="flex items-center gap-6 mb-4 text-sm sm:text-base">
                {event.rating && (
                  <div className="flex items-center gap-1.5">
                    <Star size={18} className="text-yellow-400 fill-current" />
                    <span className="text-white/90 font-medium">{event.rating.toFixed(1)}</span>
                  </div>
                )}
                {event.attendees && (
                  <div className="flex items-center gap-1.5">
                    <Users size={18} className="text-blue-400" />
                    <span className="text-white/90">{event.attendees.toLocaleString()} attendees</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="glass"
              size="lg"
              onClick={handleCardClick}
              className="flex-1 min-h-[44px] sm:min-h-[48px]"
              icon={<Eye size={18} />}
            >
              View Details
            </Button>
            <div className="flex gap-2 sm:hidden">
              <button
                onClick={handleFavoriteClick}
                className={`w-12 h-12 flex items-center justify-center rounded-xl backdrop-blur-md transition-all duration-300 hover:scale-110 active:scale-95 ${
                  isFavorited
                    ? 'bg-red-500/90 text-white shadow-lg'
                    : 'glass-button text-white'
                }`}
                aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Heart size={20} className={isFavorited ? 'fill-current' : ''} />
              </button>
              {onShare && (
                <button
                  onClick={handleShareClick}
                  className="w-12 h-12 flex items-center justify-center glass-button rounded-xl text-white hover:scale-110 active:scale-95 transition-all duration-300"
                  aria-label="Share event"
                >
                  <Share2 size={20} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default EventCard;
